/**
 * Typed wrappers for the `/salescoach/api/engagement/*` endpoints (Plan 06 Task 6.1).
 *
 * Mirrors `api-client.ts` conventions:
 *   1. Strip defaults from the filter object (short URL / cache-key friendly).
 *   2. Resolve the preset to concrete `from`/`to` so the server never has to
 *      re-interpret the preset string.
 *   3. Serialise with `qs.stringify` (arrayFormat='comma' — matches
 *      `use-url-filters`).
 *   4. Return the raw `ApiEnvelope<T>`; unwrapping is the hook's job
 *      (`use-engagement.ts`).
 *
 * Endpoints per Plan 02 + Activity-heatmap supersession:
 *   GET /kpis              → EngagementKpis
 *   GET /users-trend       → UsersTrendPoint[]
 *   GET /dau-trend         → DauTrendPoint[]
 *   GET /cohort-heatmap    → CohortRow[]
 *   GET /activity-heatmap  → ActivityHeatmapCell[]
 *   GET /top-users         → TopUserRow[] (with limit + sort params)
 */

import { apiFetch } from '@/services/api';
import { buildApiUrl } from '@/services/api-client';
import type { Filters } from '@/lib/filters-schema';
import type {
  ActivityHeatmapResponse,
  ApiEnvelope,
  CohortDrilldownResponse,
  CohortHeatmapResponse,
  DauTrendPoint,
  EngagementKpis,
  JiraActivityPoint,
  TopUserRow,
  TopUsersSortColumn,
  UsersTrendBreakdown,
  UsersTrendPoint,
} from './api-types';

// ---------------------------------------------------------------------------
// Endpoint wrappers
// ---------------------------------------------------------------------------

export async function getEngagementKpis(
  filters: Filters,
): Promise<ApiEnvelope<EngagementKpis>> {
  return apiFetch<ApiEnvelope<EngagementKpis>>(
    buildApiUrl('/salescoach/api/engagement/kpis', filters),
  );
}

export async function getUsersTrend(
  filters: Filters,
  granularity: Filters['granularity'],
): Promise<ApiEnvelope<UsersTrendPoint[]>> {
  return apiFetch<ApiEnvelope<UsersTrendPoint[]>>(
    buildApiUrl('/salescoach/api/engagement/users-trend', filters, { granularity }),
  );
}

export async function getDauTrend(
  filters: Filters,
  granularity: Filters['granularity'],
): Promise<ApiEnvelope<DauTrendPoint[]>> {
  return apiFetch<ApiEnvelope<DauTrendPoint[]>>(
    buildApiUrl('/salescoach/api/engagement/dau-trend', filters, { granularity }),
  );
}

export async function getCohortHeatmap(
  filters: Filters,
): Promise<ApiEnvelope<CohortHeatmapResponse>> {
  return apiFetch<ApiEnvelope<CohortHeatmapResponse>>(
    buildApiUrl('/salescoach/api/engagement/cohort-heatmap', filters),
  );
}

export async function getActivityHeatmap(
  filters: Filters,
): Promise<ApiEnvelope<ActivityHeatmapResponse>> {
  return apiFetch<ApiEnvelope<ActivityHeatmapResponse>>(
    buildApiUrl('/salescoach/api/engagement/activity-heatmap', filters),
  );
}

/** Top-users params — short `<col>.<dir>` sort string matches the server regex. */
export interface TopUsersParams {
  limit?: number;
  sort?: `${TopUsersSortColumn}.${'asc' | 'desc'}`;
}

export async function getTopUsers(
  filters: Filters,
  params: TopUsersParams = {},
): Promise<ApiEnvelope<TopUserRow[]>> {
  return apiFetch<ApiEnvelope<TopUserRow[]>>(
    buildApiUrl('/salescoach/api/engagement/top-users', filters, {
      limit: params.limit,
      sort: params.sort,
    }),
  );
}

/**
 * Users-trend bar-click drill-down. The `bucket` string must be the same
 * ISO timestamp the backend emitted for that bar (we don't re-truncate
 * here — the server uses `bucket` as the half-open interval start and
 * advances by one `granularity` unit to compute the end).
 */
export async function getUsersTrendBreakdown(
  filters: Filters,
  bucket: string,
  granularity: Filters['granularity'],
): Promise<ApiEnvelope<UsersTrendBreakdown>> {
  return apiFetch<ApiEnvelope<UsersTrendBreakdown>>(
    buildApiUrl('/salescoach/api/engagement/users-trend/breakdown', filters, {
      bucket,
      granularity,
    }),
  );
}

/**
 * Jira-prefill activity timeline — two per-bucket counts (generated + clicked)
 * powering the Engagement-tab Jira funnel charts. Granularity is threaded
 * through so the bucket width matches whichever the page is in ('auto' → the
 * backend resolves against the window).
 */
export async function getJiraActivityTimeline(
  filters: Filters,
  granularity: Filters['granularity'],
): Promise<ApiEnvelope<JiraActivityPoint[]>> {
  return apiFetch<ApiEnvelope<JiraActivityPoint[]>>(
    buildApiUrl(
      '/salescoach/api/engagement/jira-activity-timeline',
      filters,
      { granularity },
    ),
  );
}

/**
 * Cohort-heatmap cell drill-down. `cohortWeek` is the same ISO timestamp the
 * backend emitted on the parent `CohortRow.cohortWeek` field (or the literal
 * `'older'` sentinel for the folded row). `weekOffset` is the column index.
 */
export async function getCohortDrilldown(
  filters: Filters,
  cohortWeek: string,
  weekOffset: number,
): Promise<ApiEnvelope<CohortDrilldownResponse>> {
  return apiFetch<ApiEnvelope<CohortDrilldownResponse>>(
    buildApiUrl('/salescoach/api/engagement/cohort-drilldown', filters, {
      cohortWeek,
      weekOffset,
    }),
  );
}
